import { Address } from "./address";
import { Audience } from "./audience";
import { MembershipStatus, ValidityStatus } from "./enums";
import { Initiative } from "./initiative";
import { MembershipRecord } from "./membership-record";
import { PhoneNumber } from "./phone-number";
import { Project } from "./project";
import { StakeholderGroup } from "./stakeholder-group";
import { SupplierDetails } from "./supplier-details";
import { TopicOfInterest } from "./topic-of-interest";

export class Organisation {
  public id: number | null = null;
  public name!: string;
  public acronym: string | null = null;
  public stakeholderGroups: StakeholderGroup[] = [];
  public addresses: Address[] = [];
  public taxId: string | null = null;
  public phoneNumbers: PhoneNumber[] = [];
  public projects: Project[] = [];
  public initiatives: Initiative[] = [];
  public topics: TopicOfInterest[] = [];
  public website: string | null = null;
  public targetAudiences: Audience[] = [];
  public membershipInterest: boolean = false;
  public trainingInterest: boolean = false;
  public additionalInfo: string | null = null;
  public archived: boolean = false;
  public supplierDetails: SupplierDetails | null = null;
  public membershipRecords: MembershipRecord[] = [];

  lastModifiedBy?: string | null;
  lastModifiedOn?: string | null;

  constructor(init?: Partial<Organisation>) {
    Object.assign(this, init);
    this.membershipRecords = (init?.membershipRecords ?? []).map(
      mr => new MembershipRecord(mr)
    );
  }

  //for organisations that are also members, the only important field is having contacts associated as primary/invoicing

  validityStatus(): ValidityStatus {
    const hasCountry =
      this.addresses?.some(address => address.countryIsoCode) ?? false;

    if (!(this.name && this.acronym && (((this.stakeholderGroups?.length ?? 0) > 0) || this.supplierDetails))) {
      return ValidityStatus.INVALID;
    } else if (!(hasCountry &&
      (((this.topics?.length ?? 0) > 0) || (this.supplierDetails))
      // && ((!this.memberDetails) || (this.memberDetails && this.memberDetails.primaryContact && this.memberDetails.invoicingContact))
    )) {
      //if organisation is also supplier, the field "topics of interest' is no longer important
      return ValidityStatus.INCOMPLETE;
    } else {
      return ValidityStatus.VALID;
    }
  }

  getActiveMembershipRecords(): MembershipRecord[] {
    return this.membershipRecords.filter(record => record.isActive());
  }

  getFutureMembershipRecords(): MembershipRecord[] {
    return this.membershipRecords.filter(record => record.isFuture());
  }

  getPastMembershipRecords(): MembershipRecord[] {
    return this.membershipRecords.filter(record => record.isPast());
  }

  get membershipStatus(): MembershipStatus {
    if (this.getActiveMembershipRecords().length > 0) {
      return MembershipStatus.ACTIVE;
    } else if (this.getFutureMembershipRecords().length > 0) {
      return MembershipStatus.FUTURE;
    } else if (this.getPastMembershipRecords().length > 0) {
      return MembershipStatus.PAST;
    } else {
      return MembershipStatus.NONE;
    }
  }

  get countryCodes(): string[] {
    const orgCountries: string[] = Array.from(
      new Set(
        this.addresses
          .filter(a => a?.countryIsoCode)                     // remove null addresses + null country codes
          .map(a => a.countryIsoCode!.trim())                 // trim safely
          .filter(c => c.length > 0)                          // remove empty strings
      )
    );
    return orgCountries;
  }

  get isSupplier(): boolean {
    return !!this.supplierDetails;
  }
}
